import Env from 'lib/Env';
import { DESTINATION_GROUP, NAVIGATION_TRIGGER_TYPE, NAVIGATION_ORIGIN } from 'root/lib/SearchlightDispatcher';
import { v4 as uuidv4 } from 'uuid';
import { getTimestamp } from 'root/lib/DatetimeManager';
import {
  getIsChallengeQuest,
  getIsOnboardingQuest,
  getNextQuestId,
  getCurrentQuestId,
  getWorkshopGames,
  getIsSelectedGameComplete,
  getIsGameFailedThreeTimes,
  getSelectedGame,
  getCurrentStepConfig,
  getCurrentTFEEpisodeConfig,
  getHasSeenWorkshopOutro,
  getIsQuestInDemoMode,
  getIsFluencyStepActive,
  getIsWorkshopIntroFinished,
  getTotalQuestGamesCount,
  getMasteredGamesCount,
} from 'a/hub/selectors/workshopSelectors';
import { getFluencyVisited } from 'a/boostFluency/redux/selectors';
import { setFluencyVisited, setFluencySuppressionArmed } from 'a/boostFluency/redux/actions';
import { getProduct } from 'a/selectors/languageSelectors';
import { aquaAxios, callAsyncWithRetry, capitalizeFirstChar } from 'root/lib/utilityFunctions';
import { getApiErrorMessage } from 'a/api/apiErrors';
import { showStandardErrorPage, getErrorActionObject } from 'root/activities/components/GlobalErrorHandler/actions';
import { logRumAction, logRumError } from 'lib/DDRum';

import { getIndexFromActivityId, getQuestKeyFromQuestId, validateWorkshopDataIntegrity } from '../lib/workshopUtility';
import {
  createMetaGameActionObj,
  enablePracticeLocally,
  enablePlacticeModeInTeacherDashboardData,
  setQuestSessionId,
  clearQuestSessionId,
} from './questsActions';
import {
  reportQuestStart,
  reportQuestComplete,
  reportExitGame,
  reportQuestStepComplete,
  reportQuestStepFailure,
  reportQuestStepAutoCompleteOn3Failures,
} from './metagameSearchlight';
import { goToHubScreen, showLoadingScreen } from './metaGameActions';
import {
  openGameSuccessModal,
  openEndOfQuestsModal,
  openGameFailureModal,
  openQuestCompleteModal,
  openWorkshopStartupModals,
  openVocabSuccessModal,
  openTFESuccessModal,
  openEreaderSuccessModal,
  openEreaderEarlyExitModal,
  openNotificationModal,
} from './modalSystemActions';
import { clearSessionProgress } from './studentProgressActions';
import { saveGameState } from '../../actions/persistStateActions';
import { getAdaptivityMap } from '../../api/adaptivity';
import gameIds from '../data/GameIds';
import {
  ACTIVITY_STATUS,
  WORKSHOP_STATE_VERSION,
  SEASON_PART,
  LAUNCH_APP_DELAY,
  SEASON,
  RETRY_TIMEOUT,
} from '../data/constants';
import { fluencyStep } from '../data/workshopSteps';
import saveQuestSession from '../../api/questSession';

export const saveWorkshopProgress = () => async (dispatch, getState) => {
  const state = getState();

  const isDemoMode = getIsQuestInDemoMode(state);
  if (isDemoMode) {
    return;
  }

  const {
    currentQuestId,
    workshop,
    areAllQuestsAndChallengesComplete,
    placement,
    lastQuestFinishedBeforeEndOfQuests,
    seasonPart,
    rewards,
    myCollection,
    selectedCurioso,
    workshopStep,
    gameFailures,
    benchmark,
    bookTrack,
    isStepInProgress,
    hasSeenWorkshopOutro,
    hasSeenReplacementModal,
  } = state.workshopReducer;

  const isChallengeQuest = getIsChallengeQuest(state);
  const questKey = getQuestKeyFromQuestId(currentQuestId);
  const quest = workshop[questKey];

  // Validate workshop data integrity before saving (non-blocking)
  // Skip validation for demo mode (already exited) and challenge quests
  if (!isChallengeQuest) {
    validateWorkshopDataIntegrity({
      workshopStep,
      currentQuestId,
      benchmark,
      quest,
      validationPoint: 'beforeSave',
    });
  }

  await dispatch(saveGameState(gameIds.METAGAME_WORKSHOP,
    {
      version: WORKSHOP_STATE_VERSION,
      lastQuestFinishedBeforeEndOfQuests,
      placement,
      currentQuestId,
      seasonPart,
      questSessionId: isChallengeQuest ? '' : workshop[questKey].questSessionId,
      currentQuest: {
        ...workshop[questKey],
        updated: getTimestamp(),
        questKey,
      },
      workshopStep,
      isStepInProgress,
      isChallengeQuest,
      areAllQuestsAndChallengesComplete,
      rewards,
      myCollection,
      selectedCurioso,
      gameFailures,
      benchmark,
      bookTrack,
      hasSeenWorkshopOutro,
      hasSeenReplacementModal,
    },
  ));
};

export const saveWorkshopDataToHistory = () => (dispatch, getState) => {
  const { workshop, currentQuestId, lastQuestFinishedBeforeEndOfQuests, seasonPart } = getState().workshopReducer;
  const archivedQuests = {};
  let questPointer = seasonPart === SEASON_PART.A ? 301 : 309;
  const endQuestId = currentQuestId === 0 ? lastQuestFinishedBeforeEndOfQuests : currentQuestId;
  while (questPointer <= endQuestId) {
    archivedQuests[`quest${questPointer}`] = workshop[`quest${questPointer}`];
    questPointer += 1;
  }
  dispatch(saveGameState(gameIds.METAGAME_WORKSHOP_HISTORY,
    {
      history: { ...archivedQuests },
    },
  ));
};

export const updateChallengeQuestGames = games => (dispatch) => {
  dispatch(createMetaGameActionObj('UPDATE_CHALLENGE_QUEST_GAMES', games));
  dispatch(saveWorkshopProgress());
};

export const goToPractice = ({ trigger = 'not set' }) => (dispatch) => {
  const navigationParams = {
    destinationGroup: DESTINATION_GROUP.PRACTICE,
    origin: NAVIGATION_ORIGIN.WORKSHOP,
    trigger,
    triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
  };
  dispatch(goToHubScreen({ screenName: 'PracticePage', navigationParams }));
};

export const markAllQuestsAndChallengesComplete = () => (dispatch, getState) => {
  const product = getProduct(getState());
  dispatch(
    createMetaGameActionObj('MARK_ALL_QUESTS_AND_CHALLENGES_COMPLETE', { product }),
  );
};

export const exitActivityToWorkshop = ({ origin }) => (dispatch) => {
  const navigationParams = {
    destinationGroup: DESTINATION_GROUP.WORKSHOP,
    origin,
    trigger: 'interstitialScreen',
    triggerType: NAVIGATION_TRIGGER_TYPE.MANUAL,
  };
  dispatch(reportExitGame());
  dispatch(goToHubScreen({ screenName: DESTINATION_GROUP.WORKSHOP, navigationParams }));
};

export const selectCurioso = (curioso) =>
  createMetaGameActionObj('SELECT_CURIOSO', { curioso });

export const setTransmitterStatus = (status) => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  dispatch(createMetaGameActionObj('SET_TRANSMITTER_STATUS', { questId, status }));
};

export const setTFEStatus = (status) => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  dispatch(createMetaGameActionObj('SET_TFE_STATUS', { questId, status }));
};

export const setBatteryStatus = (status) => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  dispatch(createMetaGameActionObj('SET_BATTERY_STATUS', { questId, status }));
};

export const setEreaderStatus = (status) => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  dispatch(createMetaGameActionObj('SET_EREADER_STATUS', { questId, status }));
};

export const setTransmissionSequenceId = (transmissionSequenceId) => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  dispatch(createMetaGameActionObj(
    'SET_TRANSMISSION_SEQUENCE_ID',
    { questId, transmissionSequenceId },
  ));
};

export const setTransmissionText = (text) => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  dispatch(createMetaGameActionObj('SET_TRANSMISSION_TEXT', { questId, text }));
};

export const clearTransmissionSequence = () => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  dispatch(createMetaGameActionObj('CLEAR_TRANSMISSION_SEQUENCE', { questId }));
};

// This function should be used only on Demo mode,
// when all games are completed, we need to set the battery status to finished
const updateBatteryStatusBasedOnGames = () => (dispatch, getState) => {
  const state = getState();
  const total = getTotalQuestGamesCount(state);
  const mastered = getMasteredGamesCount(state);
  if (mastered >= total) {
    dispatch(setBatteryStatus(ACTIVITY_STATUS.FINISHED));
  }
};

// Workshop progresses as a flow composed of steps. Each step has a config object that describes how to setup state for
// that particular step. When workshop launches or when the step is changed, we need to apply the changes described in
// the config object to the redux state.
//
// `armFluencySuppression` arms (or disarms) the session-scoped flag that
// `getIsFluencyStepActive` reads to decide whether to apply the
// FLUENCY_SUPPRESSING_TRANSMISSIONS previous-step rule. The default is `false`
// so entry-point callers that run on app load / quest transition (Workshop.js
// mount, `startWorkshop`, `openNextQuest`) don't need to opt out — a narration
// played in a previous session shouldn't suppress the CTA on the restored
// step. `advanceToNextWorkshopStep` passes `true` because the student just
// heard the narration, so the rule should fire.
export const applyWorkshopStepConfig = (armFluencySuppression = false) => (dispatch, getState) => {
  dispatch(setFluencySuppressionArmed(armFluencySuppression));
  const config = getCurrentStepConfig(getState()) || {};
  if (!config) {
    return;
  }
  if (config.TFE) {
    dispatch(setTFEStatus(config.TFE));
  }
  if (config.transmitter) {
    dispatch(setTransmitterStatus(config.transmitter));
  }
  if (config.battery) {
    dispatch(setBatteryStatus(config.battery));
  }
  if (config.eReader) {
    dispatch(setEreaderStatus(config.eReader));
  }
  if (config.transmissionSequenceId !== undefined) { // null value is valid because it flags no sequence present
    dispatch(setTransmissionSequenceId(config.transmissionSequenceId));
  }
  if (config.resetCurioso) {
    dispatch(selectCurioso(null));
  }
  // special setup for the demo mode
  const isDemoMode = getIsQuestInDemoMode(getState());
  if (isDemoMode) {
    dispatch(updateBatteryStatusBasedOnGames());
  }

  dispatch(applyFluencyStepConfig());
};

// Overrides the transmitter status to CALL_TO_ACTION when a fluency assignment is active and unvisited.
// Invoked automatically at the end of applyWorkshopStepConfig() so every workshop step (re)application
// also reconciles fluency — callers never apply one without the other.
// On remount after returning from FluencyStation, fluencyVisited is true so
// applyTransmitterConfig() runs immediately without showing the notification.
export const applyFluencyStepConfig = () => (dispatch, getState) => {
  const state = getState();
  if (!getIsWorkshopIntroFinished(state) || !getIsFluencyStepActive(state)) {
    return;
  }

  const applyTransmitterConfig = () => {
    dispatch(setTransmitterStatus(fluencyStep.transmitter));
    dispatch(setTransmissionSequenceId(fluencyStep.transmissionSequenceId));
  };

  // First visit: show notification first, then apply transmitter config on close
  // so the CTA animation doesn't play underneath the notification.
  if (!getFluencyVisited(state)) {
    dispatch(openNotificationModal({
      header: 'Fluency Quest Active',
      description: 'Hone your reading skills by broadcasting to Zephraya.',
      status: 'finished', // it determines the styling of the modal
      disableAutoFade: true,
      onClose: () => {
        applyTransmitterConfig();
        dispatch(setFluencyVisited());
      },
    }));
  } else {
    // otherwise we apply the config immediately
    applyTransmitterConfig();
  }
};

/**
 * Advances the workshop to the next step in the flow.
 * This function updates the workshop step ID, sets up the new step configuration,
 * and optionally saves the current workshop progress.
 * @param {string} [source='unset'] - Caller-supplied label identifying what triggered the advancement,
 * included in the RUM log to aid debugging.
 * @param {boolean} [saveProgress=true] - Indicates whether to save the workshop progress after advancing to the next step.
 * @param {number} [openedAtStep] - Step the caller was opened for; the advance is skipped if it differs.
 */
export const advanceToNextWorkshopStep = (
  source = 'unset',
  saveProgress = true,
  openedAtStep,
) => (dispatch, getState) => {
  const state = getState();
  if (getIsChallengeQuest(state) || getIsQuestInDemoMode(state)) {
    return;
  }
  const { step: currentStep, nextStep } = getCurrentStepConfig(state);
  if (openedAtStep !== undefined && openedAtStep !== currentStep) {
    logRumError('Workshop: stale step advance ignored', { source, openedAtStep, currentStep });
    return;
  }
  if (currentStep >= nextStep) {
    return;
  }

  logRumAction(`advance Workshop step, ${source}`, { source, currentStep, nextStep, saveProgress });

  dispatch(createMetaGameActionObj('SET_WORKSHOP_STEP_ID', { step: nextStep }));
  dispatch(applyWorkshopStepConfig(true));
  if (saveProgress) {
    dispatch(saveWorkshopProgress());
  }
};

export const notifyGameLevelSuccess = (gameId, level) => (dispatch, getState) => {
  const state = getState();
  const questId = getCurrentQuestId(state);
  const games = getWorkshopGames(state);
  const selectedGame = getSelectedGame(state);
  const wasGameMastered = selectedGame?.levelsMastered?.includes(level);

  dispatch(createMetaGameActionObj('SET_QUEST_GAME_LEVEL_MASTERED', {
    questId,
    selectedGame,
    level,
  }));

  const isQuestStepComplete = getIsSelectedGameComplete(getState());
  logRumAction('Workshop: game level success', {
    gameId,
    skillId: selectedGame?.skillId,
    levelIndex: level,
    wasGameMastered,
    isQuestStepComplete,
  });
  if (isQuestStepComplete) {
    dispatch(reportQuestStepComplete({ questId, gameName: gameId, location: 'workshop', stepId: games.length }));
    dispatch(openGameSuccessModal({ isInWaitingList: true }));
    if (!getIsChallengeQuest(getState())) {
      const source = 'GameStepSuccess';
      dispatch(advanceToNextWorkshopStep(source, false));
    }
  }

  dispatch(saveWorkshopProgress());
};

export const notifyGameLevelFailed = (gameId, level) => (dispatch, getState) => {
  const gameFailureKey = `${gameId}_${level}`;
  dispatch(createMetaGameActionObj('INCREMENT_GAME_FAILURE_COUNT', { gameFailureKey }));

  const state = getState();

  const isQuestStepFailed = getIsGameFailedThreeTimes(state, gameFailureKey);
  const selectedGame = getSelectedGame(state);
  logRumAction('Workshop: game level failed', {
    gameId,
    skillId: selectedGame?.skillId,
    levelIndex: level,
    isQuestStepFailed,
  });
  if (isQuestStepFailed) {
    const questId = getCurrentQuestId(state);

    dispatch(createMetaGameActionObj('COMPLETE_STUCK_GAME', { questId, selectedGame }));
    dispatch(openGameFailureModal({ isInWaitingList: true }));
    dispatch(reportQuestStepFailure({ questId: `${questId}`, gameName: gameId, levelFailed: level }));
    dispatch(reportQuestStepAutoCompleteOn3Failures(
      {
        questId: questId.toString(),
        autoCompletedGame: gameId,
        failedLevel: level.toString(),
        season: SEASON.GRADE_4_5,
      },
    ));

    const isChallengeQuest = getIsChallengeQuest(state);
    if (!isChallengeQuest) {
      const source = 'GameStepFailed';
      dispatch(advanceToNextWorkshopStep(source, false));
    }
  }
  dispatch(saveWorkshopProgress());
};

export const fetchTFEConfig = () => async (dispatch) => {
  dispatch({
    gameId: gameIds.METAGAME,
    type: 'FETCH_TFE_CONFIG_DATA_START',
  });

  const asyncRetryFn = async () => {
    const { data } = await aquaAxios(`${Env.CLOSE_READING_URL}${Env.CLOSE_READING_CONFIG_URL}?key=${Math.random()}`, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
    });
    dispatch({
      gameId: gameIds.METAGAME,
      type: 'FETCH_TFE_CONFIG_DATA_SUCCESS',
      payload: {
        json: data,
      },
    });
  };

  const asyncRetryConfig = {
    interval: RETRY_TIMEOUT,
    logger: async (error, { isDoneRetrying }) => {
      const message = getApiErrorMessage(error);
      dispatch(getErrorActionObject(
        gameIds.METAGAME,
        message,
        'API: /content/waves/episodes.json',
        'API: fetchTFEConfig',
        'api',
      ));
      if (isDoneRetrying) {
        // This API is part of loading percentage screen.
        // Clear loading UX to show Error screen
        window.reactLoaded();
        // Show Uh Oh error screen
        dispatch(showStandardErrorPage(error.name, message));
      }
    },
  };

  return callAsyncWithRetry(asyncRetryFn, asyncRetryConfig);
};

export const setIsVocabAppAvailable = isAvailable => ({
  gameId: gameIds.METAGAME,
  type: 'SET_IS_VOCAB_APP_AVAILABLE',
  payload: isAvailable,
});

export const fetchVocabStatus = () => async (dispatch, getState) => {
  const { season } = getState().questWorkflowReducer;
  dispatch({
    gameId: gameIds.METAGAME,
    type: 'FETCH_VOCAB_STATUS_DATA_START',
  });
  try {
    const { data } = await aquaAxios(`${Env.VOCAB_STATUS_URL}?key=${Math.random()}`, {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
    });
    if (data.seasons[season].enabled) {
      // set vocab app enabled
      dispatch(setIsVocabAppAvailable(true));
    } else {
      // set vocab app disabled
      dispatch(setIsVocabAppAvailable(false));
    }
  } catch (error) {
    dispatch(setIsVocabAppAvailable(false));
  }
};

export const sendQuestSessionId = () => (dispatch, getState) => {
  const { currentQuestId, workshop } = getState().workshopReducer;
  if (currentQuestId) {
    const currentQuestData = workshop[`quest${currentQuestId}`];
    if (currentQuestData.questSessionId === '') {
      const newQuestSessionId = uuidv4();
      const contentIds = [];
      let source = 'hard-coded';
      currentQuestData.games.forEach((game) => {
        contentIds.push(game.skillId);
        if (game.source === 'adaptivity') {
          source = 'adaptivity';
        }
      });
      dispatch(setQuestSessionId(newQuestSessionId));
      saveQuestSession(currentQuestId, newQuestSessionId, `{${contentIds.join()}}`, source);
    }
  }
};

export const onStudentProgressClose = () => (dispatch, getState) => {
  const state = getState();
  if (getIsOnboardingQuest(state) && !getIsQuestInDemoMode(state)) {
    dispatch(openQuestCompleteModal());
  }
};

export const updateMyCollection = newData =>
  createMetaGameActionObj('UPDATE_MY_COLLECTION', { ...newData });

export const addToMyCollection = (tab, name) => (dispatch, getState) => {
  const { myCollection } = getState().workshopReducer;
  dispatch(updateMyCollection({
    [tab]: [...new Set([...myCollection[tab], name])],
    [`selected${capitalizeFirstChar(tab)}`]: name, // auto select the newest item
    selectedCollectionsTab: tab,
  }));
};

export const selectCollectionsTab = (tab) =>
  updateMyCollection({ selectedCollectionsTab: tab });

export const addToRewards = (type, name) =>
  createMetaGameActionObj('ADD_TO_REWARDS', { type, name });

export const selectRewardItem = (rewardType, itemName) =>
  createMetaGameActionObj('SELECT_REWARD_ITEM', { rewardType, itemName });

export const clearLastLevelSummary = () =>
  createMetaGameActionObj('CLEAR_LAST_LEVEL_SUMMARY');

export const enableTransmitter = () =>
  setTransmitterStatus(ACTIVITY_STATUS.CALL_TO_ACTION);

export const completeCurrentQuest = () => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  const isOnboardingQuest = getIsOnboardingQuest(getState());
  const hasSeenWorkshopOutro = getHasSeenWorkshopOutro(getState());
  if (questId === 316) {
    if (!hasSeenWorkshopOutro) {
      dispatch(setTFEStatus(ACTIVITY_STATUS.FINISHED_CALL_TO_ACTION));
    } else {
      dispatch(openEndOfQuestsModal());
    }
  } else if (!isOnboardingQuest) {
    if (questId === 308) {
      dispatch(openEndOfQuestsModal());
    } else {
      dispatch(openQuestCompleteModal());
    }
  }
};

export const openNextQuest = () => (dispatch, getState) => {
  const state = getState();
  const questId = getCurrentQuestId(state);
  const nextQuestId = getNextQuestId(state);

  dispatch(createMetaGameActionObj('SET_LAST_WORKSHOP_QUEST_PLAYED'));
  if (nextQuestId === 0) {
    dispatch(enablePracticeLocally(true));
    dispatch(enablePlacticeModeInTeacherDashboardData());
    dispatch(createMetaGameActionObj('SET_QUESTS_END_EXPERIENCE'));
  }

  dispatch(createMetaGameActionObj('INIT_QUEST', { questId: nextQuestId }));
  dispatch(clearSessionProgress());
  dispatch(clearQuestSessionId());

  dispatch(reportQuestComplete({ location: 'workshop', questId }));
  dispatch(reportQuestStart({ location: 'workshop', questId: nextQuestId }));

  if (nextQuestId !== 0) {
    dispatch(getAdaptivityMap());
  }

  dispatch(applyWorkshopStepConfig());
  dispatch(saveWorkshopProgress());
  dispatch(saveWorkshopDataToHistory());
};

/**
 * Sets the workshop step as in progress and optionally saves the workshop progress.
 * @param {boolean} shouldSave - Indicates whether to save the workshop progress after setting the step as in progress.
 * @returns {function} A function that takes `dispatch` as an argument and performs the action.
 */
export const setWorkshopStepAsInProgress = (shouldSave) => async (dispatch) => {
  dispatch(createMetaGameActionObj('SET_WORKSHOP_STEP_AS_IN_PROGRESS'));
  if (shouldSave) {
    await dispatch(saveWorkshopProgress());
  }
};

export const startWorkshop = () => (dispatch, getState) => {
  const config = getCurrentStepConfig(getState()) || {};

  logRumAction(`startWorkshop, step: ${config?.step}`);
  dispatch(applyWorkshopStepConfig());
  dispatch(openWorkshopStartupModals(config));
};

export const notifyEreaderStepProgress = (isComplete) => (dispatch, getState) => {
  const config = getCurrentStepConfig(getState()) || {};
  const { targetActivityId = '' } = config;
  if (targetActivityId.includes('ereader')) {
    if (isComplete) {
      dispatch(openEreaderSuccessModal());
    } else {
      dispatch(openEreaderEarlyExitModal());
    }
  }
};

// Before finishing all the quests, we need to show the outro movie on Close Reading side.
// We mark the outro as seen in the Redux store to avoid showing it again when the user comes back to the workshop.
// Once student comes back, the true flag will be used to show the Quest Complete modal and trigger Challenge Quest.
export const goToWorkshopSeasonOutro = () => async (dispatch, getState) => {
  const episodeConfig = getCurrentTFEEpisodeConfig(getState());
  const TFE_REDIRECT_URL = `${Env.CLOSE_READING_URL}${episodeConfig.gradeOutroUrl}`;
  dispatch(createMetaGameActionObj('SET_HAS_SEEN_WORKSHOP_OUTRO', { hasSeenWorkshopOutro: true }));
  dispatch(showLoadingScreen());
  await dispatch(saveWorkshopProgress());
  setTimeout(
    () => {
      window.location.href = TFE_REDIRECT_URL;
    },
    LAUNCH_APP_DELAY,
  );
};

export const markReplacementModalAsSeen = () =>
  createMetaGameActionObj('MARK_REPLACEMENT_MODAL_AS_SEEN');

// -------- DEV ONLY ACTIONS --------- /
export const autoCompleteVocabSession = (questId, sessionId) => (dispatch) => {
  dispatch(createMetaGameActionObj('AUTO_COMPLETE_VOCAB_SESSION', { questId, sessionId }));
  dispatch(openVocabSuccessModal());
};

export const autoCompleteTFESession = () => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  const { targetActivityId } = getCurrentStepConfig(getState());
  const index = getIndexFromActivityId(targetActivityId);
  dispatch(createMetaGameActionObj('AUTO_COMPLETE_TFE_SESSION', { questId, index }));
  dispatch(openTFESuccessModal());
};

export const autoCompleteEreaderSession = () => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  dispatch(createMetaGameActionObj('AUTO_COMPLETE_EREADER_SESSION', { questId }));
  dispatch(openEreaderSuccessModal());
};

export const notifyBookSuccess = () => (dispatch, getState) => {
  const questId = getCurrentQuestId(getState());
  dispatch(createMetaGameActionObj('COMPLETE_EREADER_SESSION', { questId }));
};
